import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import Popup from '../UI/Popup';
import api from '../api';
import './PricingPopup.css';
import LoadingButton from '../UI/LoadingButton';
import { AuthContext } from '../Login/AuthContext';
import { usePricing } from '../Payment/PricingContext';

const PricingPopup = ({ onClose }) => {
    const { isAuthenticated } = useContext(AuthContext);
    const navigate = useNavigate();
    const { prices, currency, loading: pricesLoading } = usePricing();
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [showBenefits, setShowBenefits] = useState(true); // Afficher les bénéfices d'abord
    const [error, setError] = useState(null);

    useEffect(() => {
        if (prices) {
            setSelectedPlan(prices.yearly?.id);
        }
    }, [prices]);

    const handlePlanSelection = (id) => {
        setSelectedPlan(id);
    };

    const handleSubmit = async () => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }

        try {
            setCheckoutLoading(true);
            
            const response = await api.post(`/checkout/${selectedPlan}`);
            
            if (response.data && response.data.checkout_url) {
                // Redirection vers l'URL de checkout Stripe
                window.location.href = response.data.checkout_url;
            }
        } catch (error) {
            console.error('Error during checkout:', error);
            setError(error.message);
        } finally {
            setCheckoutLoading(false);
        }
    };

    const handleNext = () => {
        setShowBenefits(false); // Affiche le panneau des prix après les bénéfices
    };

    if (pricesLoading) return <div>Loading...</div>;
    if (error) return <div>Error loading prices: {error}</div>;
    if (!prices) return <div>No pricing information available</div>;

    const calculateSavings = (periodPrice, monthlyPrice, months) => {
        if (!periodPrice || !monthlyPrice) return 0;
        const totalMonthlyPrice = monthlyPrice * months;
        const savings = ((totalMonthlyPrice - periodPrice) / totalMonthlyPrice) * 100;
        return Math.round(savings);
    };

    const formatPrice = (price, currency) => {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currency
        }).format(price/100);
    };

    const plans = [
        {
            id: prices.yearly?.id,
            price: formatPrice(prices.yearly?.unit_amount, prices.yearly?.currency),
            description: `= ${formatPrice(prices.yearly?.unit_amount / 12, prices.yearly?.currency)}/month, billed annually`,
            save: calculateSavings(
                prices.yearly?.unit_amount,
                prices.monthly?.unit_amount,
                12
            )
        },
        {
            id: prices.quarterly?.id,
            price: formatPrice(prices.quarterly?.unit_amount, prices.quarterly?.currency),
            description: `= ${formatPrice(prices.quarterly?.unit_amount / 3, prices.quarterly?.currency)}/month, billed quarterly`,
            save: calculateSavings(
                prices.quarterly?.unit_amount,
                prices.monthly?.unit_amount,
                3
            )
        },
        {
            id: prices.monthly?.id,
            price: formatPrice(prices.monthly?.unit_amount, prices.monthly?.currency),
            description: 'Billed monthly'
        }
    ];

    return (
        <Popup 
            onClose={onClose} 
            className="pricing-popup"
            title={showBenefits ? "Unlock Full potential" : "Choose your plan"}
        >
            <div className="choose-plan-container">
                {showBenefits ? (
                    <div className="benefits-panel">
                        <span className="limited-offer-text">Write without limits and unleash your creativity</span>

                        <div className="benefit">
                            <span className="material-symbols-outlined icon">all_inclusive</span>
                            <div>
                                <strong>Unlimited Writing</strong>
                                <p>Write without word limits. Expand, paraphrase, or edit your content endlessly with our AI-powered editor.</p>
                            </div>
                        </div>

                        <div className="benefit">
                            <span className="material-symbols-outlined icon">flash_on</span>
                            <div>
                                <strong>Fast Ebook Generator</strong>
                                <p>Generate a complete ebook from a single prompt in seconds, with over 30 languages and multiple styles available.</p>
                            </div>
                        </div>

                        <div className="benefit">
                            <span className="material-symbols-outlined icon">format_paint</span>
                            <div>
                                <strong>Advanced Editing Tools</strong>
                                <p>Customize your content with tools to change styles, rewrite sections, and adapt tone effortlessly.</p>
                            </div>
                        </div>

                        <div className="benefit">
                            <span className="material-symbols-outlined icon">language</span>
                            <div>
                                <strong>Multilingual Support</strong>
                                <p>Create and edit content in over 30 languages, opening doors to a global audience.</p>
                            </div>
                        </div>

                        <div className="benefit">
                            <span className="material-symbols-outlined icon">upload_file</span>
                            <div>
                                <strong>File Import and Export</strong>
                                <p>Import existing text files and export your projects in TXT, PDF, or Word formats without restrictions.</p>
                            </div>
                        </div>

                        <div className="benefit">
                            <span className="material-symbols-outlined icon">support_agent</span>
                            <div>
                                <strong>Priority Support</strong>
                                <p>Get priority assistance for any questions or technical issues.</p>
                            </div>
                        </div>

                        <div className="benefit">
                            <span className="material-symbols-outlined icon">update</span>
                            <div>
                                <strong>Early Access to New Features</strong>
                                <p>Be the first to explore new tools and features with regular updates based on user feedback.</p>
                            </div>
                        </div>

                        <div className="popup-buttons">
                            <button className="popup-primary-button" onClick={handleNext}>
                                <span className="material-symbols-outlined icon">arrow_forward</span> Next step
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="pricing-panel">
                        <p className="limited-offer-text">Limited Launch Offer:</p>
                        <h3 className="secure-offer-text">Save up to {Math.max(...plans.filter(p => p.save).map(p => p.save))}%</h3>

                        <div className="plan-options">
                            {plans.map((plan) => (
                                <div
                                    key={plan.id}
                                    className={`plan-option ${selectedPlan === plan.id ? 'selected' : ''}`}
                                    onClick={() => handlePlanSelection(plan.id)}
                                >
                                    {plan.save > 0 && <div className="plan-save">Save {plan.save}%</div>}
                                    {plan.tag && <div className="plan-tag">{plan.tag}</div>}
                                    <div className="plan-pricing">
                                        <span className="plan-price">{plan.price}</span>
                                    </div>
                                    <div className="plan-description">{plan.description}</div>
                                </div>
                            ))}
                        </div>

                        <div className="popup-buttons">
                            <LoadingButton
                                isLoading={checkoutLoading}
                                onClick={handleSubmit}
                                disabled={!selectedPlan}
                                className="popup-primary-button"
                            >
                                <span className="material-symbols-outlined icon">star</span>
                                Unlock Book Witch
                            </LoadingButton>
                        </div>

                        <p className="cancel-text">Cancel anytime</p>
                    </div>
                )}
            </div>
        </Popup>
    );
};

export default PricingPopup;
